/**
 * Граф-поиск маршрутных плеч (park-and-walk): Dijkstra по профилю, сход с ребра,
 * выбор плеча, drive-toward и СОВМЕСТНАЯ оптимизация пары (парковка, дверь).
 * Выделено из route.ts (лимит 500 строк); чистые функции над BuiltGraph.
 */
import { haversineM, type LonLat, polylineLengthM, projectOnSegment } from '@app/geo'
import { astar } from './astar'
import {
  type BuiltGraph,
  edgeAllows,
  edgeCostM,
  edgeProfileFactor,
  type RouteProfile,
} from './graph'
import { pointInAnyRing, segmentCrossesRings } from './route-geom'
import type { Venue } from './types'

/**
 * Штраф метра «напрямую» против метра по дороге при выборе узла схода с сети:
 * срезка почти всегда хуже дороги, прямое плечо — короткий «последний метр».
 */
export const OFF_NETWORK_COST_FACTOR = 3
/** Слишком близко к узлу — отдельная via-точка не нужна (дубль вершины). */
export const VIA_MIN_M = 1.5
/**
 * Психологический штраф пешего метра против метра за рулём при выборе пары
 * (парковка, дверь): водитель предпочитает подъехать ближе ко входу и меньше
 * идти пешком. γ>1 → из равных по сумме пар выигрывает та, где пеший «дожим»
 * короче. Значение — как в разборах park-and-walk (research/21, ≈2.5).
 */
const WALK_PENALTY = 2.5

interface DijkstraState {
  readonly dist: Map<string, number>
  readonly prev: Map<string, string>
  readonly visited: Set<string>
}

/** Ближайший непосещённый узел; площадочный масштаб — линейный минимум. */
function popNearest(state: DijkstraState): { id: string; cost: number } | null {
  let id: string | null = null
  let cost = Infinity
  for (const [candidate, d] of state.dist) {
    if (!state.visited.has(candidate) && d < cost) {
      cost = d
      id = candidate
    }
  }
  return id === null ? null : { id, cost }
}

function relaxNeighbors(
  graph: BuiltGraph,
  profile: RouteProfile,
  strict: boolean,
  state: DijkstraState,
  current: { id: string; cost: number },
): void {
  const fromNode = graph.nodes.get(current.id)
  if (!fromNode) return
  for (const entry of graph.adj.get(current.id) ?? []) {
    const toNode = graph.nodes.get(entry.to)
    if (!toNode || state.visited.has(entry.to)) continue
    if (!edgeAllows(entry.edge, profile, entry.reverse, strict)) continue
    const distM = entry.distM * edgeProfileFactor(entry.edge, profile)
    const candidate = current.cost + edgeCostM(distM, fromNode, toNode)
    if (candidate < (state.dist.get(entry.to) ?? Infinity)) {
      state.dist.set(entry.to, candidate)
      state.prev.set(entry.to, current.id)
    }
  }
}

/** Полные сетевые дистанции от узла (Dijkstra по профилю) + дерево путей. */
export function networkDistances(
  graph: BuiltGraph,
  startId: string,
  profile: RouteProfile,
  strict: boolean,
): { dist: Map<string, number>; prev: Map<string, string> } {
  const state: DijkstraState = {
    dist: new Map([[startId, 0]]),
    prev: new Map(),
    visited: new Set(),
  }
  for (;;) {
    const current = popNearest(state)
    if (current === null) return { dist: state.dist, prev: state.prev }
    state.visited.add(current.id)
    relaxNeighbors(graph, profile, strict, state, current)
  }
}

interface EdgeExit {
  endpointId: string
  via: LonLat
  cost: number
}

/**
 * Сход с СЕРЕДИНЫ ребра (запрос владельца 26.07): цель проецируется на каждый
 * сегмент дороги — маршрут идёт по сети до ближайшего «поворота» на ребре и
 * оттуда коротким пунктиром, а не до дальнего узла с длинной диагональю.
 */
export function bestEdgeExit(
  venue: Venue,
  graph: BuiltGraph,
  dist: ReadonlyMap<string, number>,
  targetPoint: LonLat,
  targetLevel: string | null,
  profile: RouteProfile,
  strict: boolean,
  /** Двери чужих зданий — через них не сходим с сети (см. foreignDoorIds). */
  foreign: ReadonlySet<string> = new Set(),
  /** Контуры чужих зданий — точка схода не должна лежать внутри них. */
  foreignRings: readonly number[][][] = [],
): EdgeExit | null {
  let best: EdgeExit | null = null
  const consider = (endpointId: string, endPt: LonLat, via: LonLat, edgeCost: number): void => {
    const d = dist.get(endpointId)
    if (d === undefined || foreign.has(endpointId)) return
    // «Поворот» внутри чужого здания — это визуальный въезд в него, отбрасываем.
    if (pointInAnyRing(via, foreignRings)) return
    // Прямое плечо от «поворота» до цели не должно срезать чужой корпус.
    if (segmentCrossesRings(via, targetPoint, foreignRings)) return
    const cost =
      d + haversineM(endPt, via) * edgeCost + OFF_NETWORK_COST_FACTOR * haversineM(via, targetPoint)
    if (!best || cost < best.cost) best = { endpointId, via, cost }
  }
  for (const edge of venue.network.edges) {
    const a = graph.nodes.get(edge.from)
    const b = graph.nodes.get(edge.to)
    if (!a || !b) continue
    if (targetLevel !== null && (a.level !== targetLevel || b.level !== targetLevel)) continue
    const proj = projectOnSegment(targetPoint, a.point, b.point)
    const factor = edgeProfileFactor(edge, profile)
    if (edgeAllows(edge, profile, false, strict)) consider(edge.from, a.point, proj.point, factor)
    if (edgeAllows(edge, profile, true, strict)) consider(edge.to, b.point, proj.point, factor)
  }
  return best
}

/** Уровень цели: фильтр рёбер для схода с середины (этажность честна). */
export function targetLevelOf(venue: Venue, targetId: string): string | null {
  return (
    venue.pois.find((p) => p.id === targetId)?.level ??
    venue.units.find((u) => u.id === targetId)?.level ??
    venue.network.nodes.find((n) => n.id === targetId)?.level ??
    null
  )
}

/** Кратчайший A* от cursor к любому узлу набора; null — все недостижимы. */
function bestLeg(
  graph: BuiltGraph,
  cursor: string,
  nodeIds: readonly string[],
  profile: RouteProfile,
  strict: boolean,
): string[] | null {
  let best: { path: string[]; lengthM: number } | null = null
  for (const doorId of nodeIds) {
    const path = astar(graph, cursor, doorId, profile, strict)
    if (!path) continue
    const points = path
      .map((id) => graph.nodes.get(id)?.point)
      .filter((p): p is LonLat => p !== undefined)
    const lengthM = polylineLengthM(points)
    if (!best || lengthM < best.lengthM) best = { path, lengthM }
  }
  return best?.path ?? null
}

/**
 * «Доехать докуда можно» к точке aim по профилю (drive-toward, R2): парковка/
 * рампа на своих дорогах недостижима — авто/фура едет к узлу, минимизирующему
 * «сеть + прямая до цели», дальше пеший дожим (не срезаем).
 */
function driveTowardLeg(
  graph: BuiltGraph,
  cursor: string,
  aim: LonLat,
  profile: RouteProfile,
  strict: boolean,
): string[] {
  const { dist, prev } = networkDistances(graph, cursor, profile, strict)
  let bestId = cursor
  let bestCost = Infinity
  for (const [id, d] of dist) {
    const p = graph.nodes.get(id)?.point
    if (!p) continue
    const cost = d + OFF_NETWORK_COST_FACTOR * haversineM(p, aim)
    if (cost < bestCost) {
      bestCost = cost
      bestId = id
    }
  }
  return pathFromTree(prev, bestId)
}

/**
 * Путь этапа + индекс перехода на пеший «дожим» (`walkFrom`, -1 — весь путь
 * одним профилем). Для «драйва» (авто/фура) к парковке/рампе: своим транспортом →
 * иначе ПЕШЕЙ сетью достигаем цель (машина заезжает по имеющейся дороге —
 * сплошная) → иначе цель недостижима транспортом: доезжаем «докуда можно»
 * (сплошная), дальше ПЕШКОМ по дорожкам к цели (пунктир). Так авто в грузовой
 * «карман» без легковых дорог доезжает и дожимает пешком (запрос владельца 27.07),
 * а не рисует длинную прямую к дальнему узлу.
 */
export function resolveLeg(
  graph: BuiltGraph,
  cursor: string,
  leg: { nodeIds: readonly string[]; profile: RouteProfile; parkNodeIds?: readonly string[] },
  strict: boolean,
): { path: string[]; walkFrom: number } | null {
  const path = bestLeg(graph, cursor, leg.nodeIds, leg.profile, strict)
  if (path) return { path, walkFrom: -1 }
  if (leg.profile !== 'vehicle' && leg.profile !== 'truck') return null
  const onFoot = bestLeg(graph, cursor, leg.nodeIds, 'foot', strict)
  if (onFoot) return { path: onFoot, walkFrom: -1 }
  const aim = leg.nodeIds.map((id) => graph.nodes.get(id)?.point).find(Boolean)
  if (!aim) return null
  // К цели проезда нет. Если рядом есть парковка — ПАРКУЕМСЯ на ближайшей
  // достижимой (сплошная, «через Парковка»), иначе доезжаем «докуда можно».
  // Куда ДОЙДЁМ пешком (ближайший к цели пеший узел) — цель пешего дожима.
  const toPark = leg.parkNodeIds?.length
    ? (bestLeg(graph, cursor, leg.parkNodeIds, leg.profile, strict) ??
      bestLeg(graph, cursor, leg.parkNodeIds, 'foot', strict))
    : null
  const footEnd = nearestNodeToPoint(graph, cursor, aim, 'foot', strict)
  const footEndPt = (footEnd && graph.nodes.get(footEnd)?.point) || aim
  const drivePath = toPark ?? driveTowardLeg(graph, cursor, footEndPt, leg.profile, strict)
  const p = drivePath[drivePath.length - 1] ?? cursor
  // Доехали ровно к финишу — весь путь одним профилем (сплошная).
  if (!footEnd || p === footEnd) return { path: drivePath, walkFrom: -1 }
  const walkPath = astar(graph, p, footEnd, 'foot', strict)
  if (!walkPath || walkPath.length <= 1) return { path: drivePath, walkFrom: -1 }
  return {
    path: [...drivePath, ...walkPath.slice(1)],
    walkFrom: Math.max(0, drivePath.length - 1),
  }
}

/** Ближайший к точке `aim` узел, достижимый профилем от cursor (для пешего финиша). */
function nearestNodeToPoint(
  graph: BuiltGraph,
  cursor: string,
  aim: LonLat,
  profile: RouteProfile,
  strict: boolean,
): string | null {
  const { dist } = networkDistances(graph, cursor, profile, strict)
  let best: string | null = null
  let bestD = Infinity
  for (const [id] of dist) {
    const pt = graph.nodes.get(id)?.point
    if (!pt) continue
    const d = haversineM(pt, aim)
    if (d < bestD) {
      bestD = d
      best = id
    }
  }
  return best
}

/** Этап прибытия: группа узлов-целей + профиль движения к ним. */
interface Leg {
  readonly nodeIds: readonly string[]
  readonly profile: RouteProfile
  readonly parkNodeIds?: readonly string[]
}

/**
 * Последовательное разрешение цепочки плеч (одномодальные цели и фолбэк
 * park-and-walk, когда пара не достижима): каждое плечо — A* своим профилем от
 * конца предыдущего. `walkFromIndex` — индекс узла, с которого начинается пеший
 * сегмент после «драйва» (пунктир); -1 — одномодальный маршрут.
 */
export function sequentialLegs(
  graph: BuiltGraph,
  startId: string,
  legs: readonly Leg[],
  strict: boolean,
): { nodeIds: string[]; walkFromIndex: number } | null {
  const st = { cursor: startId, nodeIds: [] as string[], walkFromIndex: -1, prevWasDrive: false }
  for (const leg of legs) {
    if (!appendLeg(graph, st, leg, strict)) break
  }
  return st.nodeIds.length > 0 ? { nodeIds: st.nodeIds, walkFromIndex: st.walkFromIndex } : null
}

interface SeqState {
  cursor: string
  nodeIds: string[]
  walkFromIndex: number
  prevWasDrive: boolean
}

/** Разрешает одно плечо и дописывает его в состояние; false — плечо пусто. */
function appendLeg(graph: BuiltGraph, st: SeqState, leg: Leg, strict: boolean): boolean {
  const res = resolveLeg(graph, st.cursor, leg, strict)
  if (!res || res.path.length === 0) return false
  const prevLen = st.nodeIds.length
  if (leg.profile === 'foot' && st.prevWasDrive) st.walkFromIndex = Math.max(0, prevLen - 1)
  // Переход на пешку ВНУТРИ плеча (drive-toward → пеший дожим).
  if (res.walkFrom >= 0) {
    st.walkFromIndex = prevLen === 0 ? res.walkFrom : Math.max(0, prevLen - 1 + res.walkFrom)
  }
  st.nodeIds.push(...(prevLen === 0 ? res.path : res.path.slice(1)))
  st.cursor = res.path[res.path.length - 1] ?? st.cursor
  st.prevWasDrive = leg.profile === 'vehicle' || leg.profile === 'truck'
  return true
}

/** Путь по дереву предков Dijkstra до target (сам target включён). */
function pathFromTree(prev: ReadonlyMap<string, string>, target: string): string[] {
  const path = [target]
  let cursor = target
  while (prev.has(cursor)) {
    cursor = prev.get(cursor) as string
    path.unshift(cursor)
  }
  return path
}

/**
 * СОВМЕСТНАЯ оптимизация пары (парковка, дверь) — как в SOTA park-and-walk
 * (два независимых разбора ИИ + IMDF): среди ВСЕХ парковок × ВСЕХ дверей
 * целевого здания берём пару, минимизирующую `drive(start→P) + γ·walk(P→E)`.
 * Не геометрия «ближайшая по прямой», а реальная сеть — потому «всегда по
 * логике». Парковку на пешей дороге авто «дожимает» по ней же (сплошной),
 * поэтому драйв-стоимость считаем и по пешей сети как фолбэк. Возвращает
 * узлы маршрута + индекс парковки (с неё начинается пеший пунктир); null —
 * ни одна пара не достижима по сети (тогда — последовательный фолбэк).
 */
export function parkAndWalkRoute(
  graph: BuiltGraph,
  start: string,
  parkings: readonly string[],
  doors: readonly string[],
  driveProfile: RouteProfile,
  strict: boolean,
): { nodeIds: string[]; walkFromIndex: number } | null {
  const driveTree = networkDistances(graph, start, driveProfile, strict)
  const footFromStart = networkDistances(graph, start, 'foot', strict)
  const driveCostTo = (p: string): { cost: number; prev: Map<string, string> } | null => {
    const d = driveTree.dist.get(p)
    if (d !== undefined) return { cost: d, prev: driveTree.prev }
    const f = footFromStart.dist.get(p)
    return f !== undefined ? { cost: f, prev: footFromStart.prev } : null
  }
  let best: { cost: number; drivePath: string[]; walkPath: string[] } | null = null
  for (const p of parkings) {
    const dc = driveCostTo(p)
    if (!dc) continue
    const footTree = networkDistances(graph, p, 'foot', strict)
    for (const e of doors) {
      const wc = footTree.dist.get(e)
      if (wc === undefined) continue
      const cost = dc.cost + WALK_PENALTY * wc
      if (!best || cost < best.cost) {
        best = {
          cost,
          drivePath: pathFromTree(dc.prev, p),
          walkPath: pathFromTree(footTree.prev, e),
        }
      }
    }
  }
  if (!best) return null
  const nodeIds = [...best.drivePath, ...best.walkPath.slice(1)]
  return { nodeIds, walkFromIndex: Math.max(0, best.drivePath.length - 1) }
}
